import React, { useEffect, useMemo, useState } from "react";
import "../index.css";

const REVIEW_STATUSES = [
  "pending_analyst_review",
  "needs_manual_check",
  "approved",
  "background_only",
  "discovery_only",
  "rejected",
  "all",
];

const REVIEW_ACTIONS = [
  { id: "approve", label: "Approve" },
  { id: "needs_manual_check", label: "Needs Check" },
  { id: "background_only", label: "Background Only" },
  { id: "discovery_only", label: "Discovery Only" },
  { id: "reject", label: "Reject" },
];

const REVIEW_STATUS_BY_ACTION = {
  approve: "approved",
  reject: "rejected",
  needs_manual_check: "needs_manual_check",
  background_only: "background_only",
  discovery_only: "discovery_only",
};

const EDITABLE_FIELDS = [
  "source_name",
  "document_title",
  "country",
  "region",
  "city",
  "category",
  "source_tier",
  "source_tier_label",
  "confidence",
  "as_of_date",
  "rights_status",
  "valuation_use",
  "notes",
];

const SOURCE_NAME_OPTIONS = [
  "AAHOA",
  "AHLA",
  "Austin Development Services",
  "Austin Open Data Building Permits",
  "BEA",
  "BLS",
  "CBRE",
  "Central Bank of Ireland",
  "Central Statistics Office Ireland",
  "Colliers",
  "CoStar",
  "Cushman & Wakefield",
  "Deloitte",
  "EY",
  "European Central Bank",
  "European Union source",
  "Eurostat",
  "FAA / TSA",
  "Failte Ireland",
  "Federal Reserve",
  "FRED",
  "Government source",
  "Hospitality Net",
  "Hotel News Resource",
  "HotStats",
  "HVS",
  "Irish government source",
  "JLL",
  "KPMG",
  "Knight Frank",
  "Lodging Econometrics",
  "New York City Department of Buildings",
  "New York City Department of City Planning",
  "NYC Open Data - DOB Permit Issuance",
  "PwC",
  "Savills",
  "Skift",
  "STR",
  "STR / CoStar",
  "Tourism Economics",
  "Tourism Ireland",
  "US Census",
];

const COUNTRY_OPTIONS = ["Global", "Ireland", "USA", "United States", "United Kingdom", "Australia"];

const REGION_OPTIONS_BY_COUNTRY = {
  Global: ["Global"],
  Ireland: ["Leinster", "Munster", "Connacht", "Ulster"],
  USA: [
    "Alabama",
    "Alaska",
    "Arizona",
    "Arkansas",
    "California",
    "Colorado",
    "Connecticut",
    "Delaware",
    "District of Columbia",
    "Florida",
    "Georgia",
    "Hawaii",
    "Idaho",
    "Illinois",
    "Indiana",
    "Iowa",
    "Kansas",
    "Kentucky",
    "Louisiana",
    "Maine",
    "Maryland",
    "Massachusetts",
    "Michigan",
    "Minnesota",
    "Mississippi",
    "Missouri",
    "Montana",
    "Nebraska",
    "Nevada",
    "New Hampshire",
    "New Jersey",
    "New Mexico",
    "New York",
    "North Carolina",
    "North Dakota",
    "Ohio",
    "Oklahoma",
    "Oregon",
    "Pennsylvania",
    "Rhode Island",
    "South Carolina",
    "South Dakota",
    "Tennessee",
    "Texas",
    "Utah",
    "Vermont",
    "Virginia",
    "Washington",
    "West Virginia",
    "Wisconsin",
    "Wyoming",
  ],
  "United States": [
    "Alabama",
    "Alaska",
    "Arizona",
    "Arkansas",
    "California",
    "Colorado",
    "Connecticut",
    "Delaware",
    "District of Columbia",
    "Florida",
    "Georgia",
    "Hawaii",
    "Idaho",
    "Illinois",
    "Indiana",
    "Iowa",
    "Kansas",
    "Kentucky",
    "Louisiana",
    "Maine",
    "Maryland",
    "Massachusetts",
    "Michigan",
    "Minnesota",
    "Mississippi",
    "Missouri",
    "Montana",
    "Nebraska",
    "Nevada",
    "New Hampshire",
    "New Jersey",
    "New Mexico",
    "New York",
    "North Carolina",
    "North Dakota",
    "Ohio",
    "Oklahoma",
    "Oregon",
    "Pennsylvania",
    "Rhode Island",
    "South Carolina",
    "South Dakota",
    "Tennessee",
    "Texas",
    "Utah",
    "Vermont",
    "Virginia",
    "Washington",
    "West Virginia",
    "Wisconsin",
    "Wyoming",
  ],
  "United Kingdom": ["Northern Ireland"],
  Australia: [],
};

const CITY_OPTIONS_BY_REGION = {
  "Ireland|Leinster": ["Dublin", "Dundalk"],
  "Ireland|Munster": ["Cork", "Limerick"],
  "Ireland|Connacht": ["Galway"],
  "United Kingdom|Northern Ireland": ["Belfast"],
  "USA|Arizona": ["Phoenix"],
  "USA|California": ["Los Angeles", "Sacramento"],
  "USA|Colorado": ["Denver"],
  "USA|Florida": ["Miami"],
  "USA|Louisiana": ["New Orleans"],
  "USA|Massachusetts": ["Boston"],
  "USA|Michigan": ["Detroit"],
  "USA|Missouri": ["Kansas City"],
  "USA|Nevada": ["Las Vegas"],
  "USA|New York": ["New York City"],
  "USA|Texas": ["Austin", "Dallas", "Houston", "San Antonio"],
  "United States|Arizona": ["Phoenix"],
  "United States|California": ["Los Angeles", "Sacramento"],
  "United States|Colorado": ["Denver"],
  "United States|Florida": ["Miami"],
  "United States|Louisiana": ["New Orleans"],
  "United States|Massachusetts": ["Boston"],
  "United States|Michigan": ["Detroit"],
  "United States|Missouri": ["Kansas City"],
  "United States|Nevada": ["Las Vegas"],
  "United States|New York": ["New York City"],
  "United States|Texas": ["Austin", "Dallas", "Houston", "San Antonio"],
};

const CATEGORY_OPTIONS = [
  "capital_markets",
  "demand_airport",
  "forecasting",
  "hotel_news",
  "hotel_performance",
  "labor_market",
  "local_demographics",
  "local_economic_conditions",
  "macro_capital_markets",
  "market_research",
  "official_tourism",
  "planning",
  "policy",
  "population",
  "pipeline",
  "supply_planning",
  "supply_pipeline",
  "transactions",
  "tourism_events",
  "valuation",
];

const SOURCE_TIER_OPTIONS = [
  { value: "tier_1_official_primary", label: "Tier 1 - official / primary" },
  { value: "tier_2_professional_research", label: "Tier 2 - professional / sector research" },
  { value: "tier_3_reputable_press", label: "Tier 3 - reputable press" },
  { value: "tier_4_web_lead", label: "Tier 4 - web/search lead" },
];

const SOURCE_TIER_LABELS = Object.fromEntries(
  SOURCE_TIER_OPTIONS.map((option) => [option.value, option.label]),
);

const CONFIDENCE_OPTIONS = ["high", "medium", "low"];

const RIGHTS_STATUS_OPTIONS = [
  "public_web_source_pending_review",
  "public_api_source_pending_review",
  "public_api_or_public_web_source_pending_review",
  "public_or_licensed_source_review_required",
  "licensed_or_rights_sensitive_review_required",
  "internal_commentary_pending_review",
];

const VALUATION_USE_OPTIONS = [
  "Prepopulated market-source evidence; reviewer must approve before report use.",
  "Approved citable market evidence for paid-review reports.",
  "Background context only; do not cite directly in paid-review reports.",
  "Discovery lead only; use to find a more specific citable document.",
  "Do not use for valuation reports.",
];

const CLIENT_DOCUMENT_STEPS = [
  {
    label: "Virus scan",
    status: "placeholder",
    detail: "Not wired yet. Files remain private/quarantined after upload.",
  },
  {
    label: "OCR / extract",
    status: "placeholder",
    detail: "EMPIRE worker exists locally; Skyward trigger is not wired yet.",
  },
  {
    label: "Analyst review",
    status: "active",
    detail: "Approve snippets only after extraction and confidentiality review.",
  },
  {
    label: "Report use",
    status: "guarded",
    detail: "Only approved snippets can flow to EMPIRE analyst packs.",
  },
];

const STATIC_SELECT_OPTIONS = {
  source_name: SOURCE_NAME_OPTIONS,
  country: COUNTRY_OPTIONS,
  category: CATEGORY_OPTIONS,
  source_tier: SOURCE_TIER_OPTIONS.map((option) => option.value),
  confidence: CONFIDENCE_OPTIONS,
  rights_status: RIGHTS_STATUS_OPTIONS,
  valuation_use: VALUATION_USE_OPTIONS,
};

const UPLOAD_DEFAULTS = {
  source_name: "CBRE",
  document_title: "",
  source_url: "",
  country: "Ireland",
  region: "",
  city: "",
  category: "market_research",
  as_of_date: "2026",
  rights_status: "public_web_source_pending_review",
  valuation_use:
    "Prepopulated market-source evidence; reviewer must approve before report use.",
  notes: "",
};

function selectOptionsForField(field, values = {}) {
  if (field === "region") {
    return REGION_OPTIONS_BY_COUNTRY[values.country] || [];
  }
  if (field === "city") {
    return CITY_OPTIONS_BY_REGION[`${values.country}|${values.region}`] || [];
  }
  if (field === "source_tier_label") {
    return Object.values(SOURCE_TIER_LABELS);
  }
  return STATIC_SELECT_OPTIONS[field] || null;
}

function optionsWithCurrent(options = [], currentValue) {
  const cleanCurrent = shortText(currentValue, "");
  const values = options.filter(Boolean);
  if (cleanCurrent && !values.includes(cleanCurrent)) {
    return [cleanCurrent, ...values];
  }
  return values;
}

function shortText(value, fallback = "n/a") {
  const text = String(value || "").trim();
  return text || fallback;
}

function statusLabel(value) {
  return shortText(value).replaceAll("_", " ");
}

function base64UrlEncodeText(value) {
  const bytes = new TextEncoder().encode(String(value || ""));
  let binary = "";
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
}

function AdminMarketSourcesScreen() {
  const [adminKey, setAdminKey] = useState("");
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [filters, setFilters] = useState({
    status: "pending_analyst_review",
    country: "",
    region: "",
    city: "",
    category: "",
    source_name: "",
    search: "",
  });
  const [documents, setDocuments] = useState([]);
  const [counts, setCounts] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [reviewer, setReviewer] = useState("Anthony");
  const [reviewNotes, setReviewNotes] = useState("");
  const [evidenceLocation, setEvidenceLocation] = useState({
    country: "USA",
    region: "New York",
    city: "New York City",
  });
  const [approvedEvidence, setApprovedEvidence] = useState([]);
  const [paidReviewDocuments, setPaidReviewDocuments] = useState([]);
  const [paidReviewPolicy, setPaidReviewPolicy] = useState("");
  const [isLoadingPaidReviewDocuments, setIsLoadingPaidReviewDocuments] =
    useState(false);
  const [uploadValues, setUploadValues] = useState(UPLOAD_DEFAULTS);
  const [uploadFile, setUploadFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [message, setMessage] = useState("");

  const authHeaders = useMemo(
    () => ({
      "Content-Type": "application/json",
      "x-skyward-admin-key": adminKey,
    }),
    [adminKey],
  );

  const requestJson = async (url, options = {}) => {
    const response = await fetch(url, {
      ...options,
      headers: {
        ...authHeaders,
        ...(options.headers || {}),
      },
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.message || "Request failed.");
    }
    return data;
  };

  const queryString = (values) => {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(values)) {
      if (value) params.set(key, value);
    }
    return params.toString();
  };

  const loadDocuments = async (filterOverride = filters, options = {}) => {
    if (!adminKey) {
      setMessage("Enter the admin password to continue.");
      return;
    }
    setIsLoading(true);
    if (!options.keepMessage) {
      setMessage("");
    }
    try {
      const data = await requestJson(
        `/api/admin/market-sources?${queryString(filterOverride)}`,
      );
      setIsAuthenticated(true);
      const nextDocuments = data.documents || [];
      setDocuments(nextDocuments);
      setCounts(data.counts || []);
      if (nextDocuments.length) {
        const preferredSelectedId = options.preferredSelectedId ?? selectedId;
        const selectedStillVisible = nextDocuments.some(
          (document) => document.document_id === preferredSelectedId,
        );
        if (!selectedStillVisible) {
          setSelectedId(nextDocuments[0].document_id);
          setSelectedDocument(null);
        } else if (preferredSelectedId !== selectedId) {
          setSelectedId(preferredSelectedId);
        }
      } else {
        setSelectedId("");
        setSelectedDocument(null);
      }
    } catch (error) {
      if (String(error.message || "").toLowerCase().includes("admin")) {
        setIsAuthenticated(false);
        setDocuments([]);
        setCounts([]);
        setSelectedId("");
        setSelectedDocument(null);
      }
      setMessage(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const updateFiltersAndLoad = async (updates) => {
    const nextFilters =
      typeof updates === "function" ? updates(filters) : { ...filters, ...updates };
    setFilters(nextFilters);
    await loadDocuments(nextFilters);
  };

  const loadSelectedDocument = async (documentId) => {
    if (!adminKey || !documentId) return;
    try {
      const data = await requestJson(`/api/admin/market-sources/${documentId}`);
      setSelectedDocument(data.document);
      const nextEdits = {};
      for (const field of EDITABLE_FIELDS) {
        nextEdits[field] = data.document?.[field] || "";
      }
      setEditValues(nextEdits);
      setReviewNotes(data.document?.review_notes || "");
    } catch (error) {
      setMessage(error.message);
    }
  };

  const loadApprovedEvidence = async () => {
    if (!adminKey) return;
    try {
      const data = await requestJson(
        `/api/admin/market-sources/evidence-preview?${queryString(evidenceLocation)}`,
      );
      setApprovedEvidence(data.evidence || []);
      setMessage(`Approved evidence loaded: ${data.count || 0}`);
    } catch (error) {
      setMessage(error.message);
    }
  };

  const loadPaidReviewDocuments = async () => {
    if (!adminKey) return;
    setIsLoadingPaidReviewDocuments(true);
    setMessage("");
    try {
      const data = await requestJson("/api/admin/paid-review-documents?limit=50");
      setPaidReviewDocuments(data.documents || []);
      setPaidReviewPolicy(data.policy || "");
      setMessage(`Client paid-review documents loaded: ${data.count || 0}`);
    } catch (error) {
      setMessage(error.message);
    } finally {
      setIsLoadingPaidReviewDocuments(false);
    }
  };

  const updateEditValue = (field, value) => {
    setEditValues((prev) => {
      const next = { ...prev, [field]: value };
      if (field === "country") {
        const validRegions = REGION_OPTIONS_BY_COUNTRY[value] || [];
        if (next.region && !validRegions.includes(next.region)) {
          next.region = "";
        }
        next.city = "";
      }
      if (field === "region") {
        const validCities = CITY_OPTIONS_BY_REGION[`${next.country}|${value}`] || [];
        if (next.city && !validCities.includes(next.city)) {
          next.city = "";
        }
      }
      if (field === "source_tier") {
        next.source_tier_label = SOURCE_TIER_LABELS[value] || "";
      }
      return next;
    });
  };

  const updateUploadValue = (field, value) => {
    setUploadValues((prev) => {
      const next = { ...prev, [field]: value };
      if (field === "country") {
        const validRegions = REGION_OPTIONS_BY_COUNTRY[value] || [];
        if (next.region && !validRegions.includes(next.region)) {
          next.region = "";
        }
        next.city = "";
      }
      if (field === "region") {
        const validCities = CITY_OPTIONS_BY_REGION[`${next.country}|${value}`] || [];
        if (next.city && !validCities.includes(next.city)) {
          next.city = "";
        }
      }
      return next;
    });
  };

  useEffect(() => {
    if (selectedId) loadSelectedDocument(selectedId);
  }, [selectedId]);

  const uploadMarketSource = async (event) => {
    event.preventDefault();
    if (!uploadFile) {
      setMessage("Choose a PDF file first.");
      return;
    }
    if (
      !uploadValues.source_name ||
      !uploadValues.document_title ||
      !uploadValues.country ||
      !uploadValues.category
    ) {
      setMessage("Source, title, country, and category are required.");
      return;
    }
    setIsUploading(true);
    setMessage("");
    try {
      const response = await fetch("/api/admin/market-sources/upload", {
        method: "POST",
        headers: {
          "Content-Type": uploadFile.type || "application/pdf",
          "x-skyward-admin-key": adminKey,
          "x-skyward-file-name": uploadFile.name,
          "x-skyward-reviewer": reviewer,
          "x-skyward-market-source-metadata": base64UrlEncodeText(
            JSON.stringify(uploadValues),
          ),
        },
        body: uploadFile,
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.message || "Upload failed.");
      }
      setMessage("Uploaded for review.");
      setUploadFile(null);
      setUploadValues(UPLOAD_DEFAULTS);
      const nextFilters = { ...filters, status: "pending_analyst_review" };
      setFilters(nextFilters);
      await loadDocuments(nextFilters, {
        preferredSelectedId: data.document?.document_id,
        keepMessage: true,
      });
      if (data.document?.document_id) {
        setSelectedId(data.document.document_id);
      }
    } catch (error) {
      setMessage(error.message);
    } finally {
      setIsUploading(false);
    }
  };

  const saveEdits = async () => {
    if (!selectedDocument) return;
    setIsSaving(true);
    setMessage("");
    try {
      const data = await requestJson(
        `/api/admin/market-sources/${selectedDocument.document_id}`,
        {
          method: "PATCH",
          body: JSON.stringify({ edits: editValues }),
        },
      );
      setSelectedDocument(data.document);
      setMessage("Saved.");
      await loadDocuments(filters, {
        preferredSelectedId: data.document?.document_id,
        keepMessage: true,
      });
    } catch (error) {
      setMessage(error.message);
    } finally {
      setIsSaving(false);
    }
  };

  const applyReviewAction = async (action) => {
    if (!selectedDocument) return;
    setIsSaving(true);
    setMessage("");
    try {
      const data = await requestJson(
        `/api/admin/market-sources/${selectedDocument.document_id}/review`,
        {
          method: "POST",
          body: JSON.stringify({
            action,
            reviewer,
            notes: reviewNotes,
            edits: editValues,
          }),
        },
      );
      setSelectedDocument(data.document);
      const nextStatus =
        data.document?.review_status || REVIEW_STATUS_BY_ACTION[action] || filters.status;
      setMessage(`Review saved: ${statusLabel(nextStatus)}`);
      await loadDocuments(filters, { keepMessage: true });
    } catch (error) {
      setMessage(error.message);
    } finally {
      setIsSaving(false);
    }
  };

  const openVaultFile = async () => {
    if (!selectedDocument) return;
    setMessage("");
    const viewerWindow = window.open("about:blank", "_blank");
    if (viewerWindow) {
      viewerWindow.opener = null;
      viewerWindow.document.title = "Opening market source document";
      viewerWindow.document.body.innerHTML =
        "<p style=\"font-family:Arial,sans-serif;padding:24px;\">Opening market source document...</p>";
    } else {
      setMessage("Your browser blocked the document window. Allow pop-ups for this admin page and try again.");
      return;
    }
    try {
      const response = await fetch(
        `/api/admin/market-sources/${selectedDocument.document_id}/file-link`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-skyward-admin-key": adminKey,
          },
        },
      );
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.message || "Could not open document.");
      }
      const data = await response.json();
      if (!data.fileUrl) {
        throw new Error("Document link was not returned.");
      }
      viewerWindow.location.href = data.fileUrl;
    } catch (error) {
      viewerWindow.document.body.innerHTML = `<p style="font-family:Arial,sans-serif;padding:24px;">${shortText(
        error.message,
        "Could not open document.",
      )}</p>`;
      setMessage(error.message);
    }
  };

  const signOut = () => {
    setAdminKey("");
    setIsAuthenticated(false);
    setDocuments([]);
    setCounts([]);
    setSelectedId("");
    setSelectedDocument(null);
    setApprovedEvidence([]);
    setPaidReviewDocuments([]);
    setPaidReviewPolicy("");
    setMessage("");
  };

  const countMap = Object.fromEntries(
    counts.map((item) => [item._id || "unknown", item.count]),
  );

  if (!isAuthenticated) {
    return (
      <main className="admin-market-page">
        <section className="admin-login-card">
          <p className="admin-kicker">Skyward Admin</p>
          <h1>Market Source Review</h1>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              loadDocuments();
            }}
          >
            <label>
              Admin password
              <input
                type="password"
                value={adminKey}
                onChange={(event) => setAdminKey(event.target.value)}
                autoComplete="current-password"
                autoFocus
              />
            </label>
            <button type="submit" disabled={isLoading}>
              {isLoading ? "Checking" : "Enter"}
            </button>
          </form>
          {message ? <div className="admin-message">{message}</div> : null}
        </section>
      </main>
    );
  }

  return (
    <main className="admin-market-page">
      <section className="admin-market-shell">
        <header className="admin-market-header">
          <div>
            <p className="admin-kicker">Skyward Admin</p>
            <h1>Market Source Review</h1>
          </div>
          <div className="admin-session-panel">
            <span>Admin session active</span>
            <button type="button" onClick={loadDocuments} disabled={isLoading}>
              {isLoading ? "Refreshing" : "Refresh"}
            </button>
            <button type="button" onClick={signOut}>
              Sign Out
            </button>
          </div>
        </header>

        {message ? <div className="admin-message">{message}</div> : null}

        <section className="admin-client-doc-panel">
          <div className="admin-client-doc-head">
            <div>
              <p className="admin-kicker">Private Client Vault</p>
              <h2>Paid-Review Client Documents</h2>
              <p>
                Client files are tied to the hotel review, held in private
                quarantine, and cannot be used in analyst packs until extracted
                and snippet-approved.
              </p>
            </div>
            <button
              type="button"
              onClick={loadPaidReviewDocuments}
              disabled={isLoadingPaidReviewDocuments}
            >
              {isLoadingPaidReviewDocuments ? "Loading" : "Refresh Client Docs"}
            </button>
          </div>

          <div className="admin-client-flow">
            {CLIENT_DOCUMENT_STEPS.map((step) => (
              <article key={step.label}>
                <strong>{step.label}</strong>
                <span>{statusLabel(step.status)}</span>
                <p>{step.detail}</p>
              </article>
            ))}
          </div>

          <div className="admin-placeholder-actions">
            <button type="button" disabled title="Coming next: scan before extraction.">
              Run Virus Scan
            </button>
            <button
              type="button"
              disabled
              title="Coming next: trigger the EMPIRE extraction worker from Skyward."
            >
              Run OCR Extraction
            </button>
            <button
              type="button"
              disabled
              title="Coming next: approve extracted snippets after OCR."
            >
              Approve Snippets
            </button>
            <button
              type="button"
              disabled
              title="Guarded: only approved snippets flow to reports."
            >
              Send Approved Snippets To Pack
            </button>
          </div>

          {paidReviewPolicy ? (
            <p className="admin-client-policy">{paidReviewPolicy}</p>
          ) : null}

          <div className="admin-client-doc-list">
            {paidReviewDocuments.map((item) => (
              <article
                key={`${item.hotelDocId}-${item.document.fileId}`}
                className="admin-client-doc-row"
              >
                <div>
                  <strong>{shortText(item.document.filename)}</strong>
                  <span>{shortText(item.property, "Unknown property")}</span>
                  <small>
                    {statusLabel(item.document.reviewStatus)} ·{" "}
                    {statusLabel(item.document.virusScanStatus)} ·{" "}
                    {statusLabel(item.document.extractionStatus)} ·{" "}
                    {statusLabel(item.document.ocrStatus)}
                  </small>
                </div>
                <dl>
                  <dt>Vault</dt>
                  <dd>{statusLabel(item.document.vaultStatus)}</dd>
                  <dt>Virus scan</dt>
                  <dd>{statusLabel(item.document.virusScanStatus)}</dd>
                  <dt>AI use</dt>
                  <dd>{statusLabel(item.document.aiUseStatus)}</dd>
                  <dt>Approved snippets</dt>
                  <dd>{item.document.approvedSnippetCount || 0}</dd>
                  <dt>Uploaded</dt>
                  <dd>{shortText(item.document.uploadedAt)}</dd>
                </dl>
              </article>
            ))}
            {!paidReviewDocuments.length ? (
              <div className="admin-empty">
                No client documents loaded. Click Refresh Client Docs after a
                paid-review upload.
              </div>
            ) : null}
          </div>
        </section>

        <section className="admin-upload-panel">
          <form onSubmit={uploadMarketSource}>
            <div>
              <p className="admin-kicker">Manual Upload</p>
              <h2>Market Source PDF</h2>
            </div>
            <label>
              PDF file
              <input
                type="file"
                accept="application/pdf,.pdf"
                onChange={(event) =>
                  setUploadFile(event.target.files?.[0] || null)
                }
              />
            </label>
            <label>
              Source
              <select
                value={uploadValues.source_name}
                onChange={(event) =>
                  updateUploadValue("source_name", event.target.value)
                }
              >
                <option value="">Select source</option>
                {SOURCE_NAME_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Title
              <input
                value={uploadValues.document_title}
                onChange={(event) =>
                  updateUploadValue("document_title", event.target.value)
                }
              />
            </label>
            <label>
              Source URL
              <input
                value={uploadValues.source_url}
                onChange={(event) =>
                  updateUploadValue("source_url", event.target.value)
                }
              />
            </label>
            <label>
              Country
              <select
                value={uploadValues.country}
                onChange={(event) =>
                  updateUploadValue("country", event.target.value)
                }
              >
                <option value="">Select country</option>
                {COUNTRY_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Region
              <select
                value={uploadValues.region}
                onChange={(event) =>
                  updateUploadValue("region", event.target.value)
                }
              >
                <option value="">Select region</option>
                {optionsWithCurrent(
                  selectOptionsForField("region", uploadValues),
                  uploadValues.region,
                ).map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </label>
            <label>
              City
              <select
                value={uploadValues.city}
                onChange={(event) =>
                  updateUploadValue("city", event.target.value)
                }
              >
                <option value="">Select city</option>
                {optionsWithCurrent(
                  selectOptionsForField("city", uploadValues),
                  uploadValues.city,
                ).map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Category
              <select
                value={uploadValues.category}
                onChange={(event) =>
                  updateUploadValue("category", event.target.value)
                }
              >
                <option value="">Select category</option>
                {CATEGORY_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {statusLabel(option)}
                  </option>
                ))}
              </select>
            </label>
            <label>
              As of
              <input
                value={uploadValues.as_of_date}
                onChange={(event) =>
                  updateUploadValue("as_of_date", event.target.value)
                }
              />
            </label>
            <label>
              Rights
              <select
                value={uploadValues.rights_status}
                onChange={(event) =>
                  updateUploadValue("rights_status", event.target.value)
                }
              >
                <option value="">Select rights</option>
                {RIGHTS_STATUS_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {statusLabel(option)}
                  </option>
                ))}
              </select>
            </label>
            <label className="admin-upload-wide">
              Notes
              <textarea
                value={uploadValues.notes}
                onChange={(event) =>
                  updateUploadValue("notes", event.target.value)
                }
              />
            </label>
            <button type="submit" disabled={isUploading}>
              {isUploading ? "Uploading" : "Upload For Review"}
            </button>
          </form>
        </section>

        <section className="admin-status-strip">
          {REVIEW_STATUSES.filter((status) => status !== "all").map((status) => (
            <button
              key={status}
              type="button"
              className={filters.status === status ? "active" : ""}
              onClick={() => updateFiltersAndLoad({ status })}
              disabled={isLoading}
            >
              {statusLabel(status)}
              <span>{countMap[status] || 0}</span>
            </button>
          ))}
        </section>

        <section className="admin-market-grid">
          <aside className="admin-source-list">
            <div className="admin-filter-grid">
              <select
                value={filters.status}
                onChange={(event) =>
                  updateFiltersAndLoad({ status: event.target.value })
                }
              >
                {REVIEW_STATUSES.map((status) => (
                  <option key={status} value={status}>
                    {statusLabel(status)}
                  </option>
                ))}
              </select>
              <input
                value={filters.country}
                onChange={(event) =>
                  setFilters((prev) => ({ ...prev, country: event.target.value }))
                }
                placeholder="Country"
              />
              <input
                value={filters.region}
                onChange={(event) =>
                  setFilters((prev) => ({ ...prev, region: event.target.value }))
                }
                placeholder="Region"
              />
              <input
                value={filters.city}
                onChange={(event) =>
                  setFilters((prev) => ({ ...prev, city: event.target.value }))
                }
                placeholder="City"
              />
              <input
                value={filters.category}
                onChange={(event) =>
                  setFilters((prev) => ({ ...prev, category: event.target.value }))
                }
                placeholder="Category"
              />
              <input
                value={filters.search}
                onChange={(event) =>
                  setFilters((prev) => ({ ...prev, search: event.target.value }))
                }
                placeholder="Search"
              />
              <button type="button" onClick={loadDocuments} disabled={isLoading}>
                Apply
              </button>
            </div>

            <div className="admin-source-results">
              {documents.map((document) => (
                <button
                  type="button"
                  key={document.document_id}
                  className={
                    selectedId === document.document_id
                      ? "admin-source-row active"
                      : "admin-source-row"
                  }
                  onClick={() => setSelectedId(document.document_id)}
                >
                  <span>{shortText(document.source_name)}</span>
                  <strong>{shortText(document.document_title)}</strong>
                  <small>
                    {[document.city, document.region, document.country]
                      .filter(Boolean)
                      .join(", ") || "Unscoped"}{" "}
                    · {statusLabel(document.review_status)}
                  </small>
                </button>
              ))}
              {!documents.length ? (
                <div className="admin-empty">No documents found.</div>
              ) : null}
            </div>
          </aside>

          <section className="admin-source-detail">
            {selectedDocument ? (
              <>
                <div className="admin-detail-head">
                  <div>
                    <p className="admin-kicker">{selectedDocument.document_id}</p>
                    <h2>{shortText(selectedDocument.document_title)}</h2>
                    <p>
                      {shortText(selectedDocument.source_name)} ·{" "}
                      {statusLabel(selectedDocument.review_status)} ·{" "}
                      {selectedDocument.approved_for_reports ? "citable" : "not citable"}
                    </p>
                  </div>
                  <div className="admin-detail-actions">
                    {selectedDocument.canonical_url ? (
                      <a
                        href={selectedDocument.canonical_url}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Source Page
                      </a>
                    ) : null}
                    {selectedDocument.original_url &&
                    selectedDocument.original_url !== selectedDocument.canonical_url ? (
                      <a
                        href={selectedDocument.original_url}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Original URL
                      </a>
                    ) : null}
                    <button type="button" onClick={openVaultFile}>
                      Open Vault File
                    </button>
                  </div>
                </div>

                <div className="admin-edit-grid">
                  {EDITABLE_FIELDS.map((field) => (
                    <label
                      key={field}
                      className={
                        field === "notes" || field === "valuation_use"
                          ? "admin-wide-field"
                          : ""
                      }
                    >
                      {field.replaceAll("_", " ")}
                      {field === "notes" || field === "valuation_use" ? (
                        field === "valuation_use" ? (
                          <select
                            value={editValues[field] || ""}
                            onChange={(event) =>
                              updateEditValue(field, event.target.value)
                            }
                          >
                            <option value="">Select valuation use</option>
                            {optionsWithCurrent(
                              selectOptionsForField(field, editValues),
                              editValues[field],
                            ).map((option) => (
                              <option key={option} value={option}>
                                {statusLabel(option)}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <textarea
                            value={editValues[field] || ""}
                            onChange={(event) =>
                              updateEditValue(field, event.target.value)
                            }
                          />
                        )
                      ) : selectOptionsForField(field, editValues) ? (
                        <select
                          value={editValues[field] || ""}
                          disabled={field === "source_tier_label"}
                          onChange={(event) =>
                            updateEditValue(field, event.target.value)
                          }
                        >
                          <option value="">Select {field.replaceAll("_", " ")}</option>
                          {optionsWithCurrent(
                            selectOptionsForField(field, editValues),
                            editValues[field],
                          ).map((option) => (
                            <option key={option} value={option}>
                              {field === "source_tier"
                                ? SOURCE_TIER_LABELS[option] || statusLabel(option)
                                : statusLabel(option)}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <input
                          value={editValues[field] || ""}
                          onChange={(event) =>
                            updateEditValue(field, event.target.value)
                          }
                        />
                      )}
                    </label>
                  ))}
                </div>

                <div className="admin-preview-panel">
                  <h3>Extracted Preview</h3>
                  <p>
                    {selectedDocument.extracted_text_preview ||
                      "No extracted preview is currently stored in Mongo."}
                  </p>
                  <dl>
                    <dt>Extraction</dt>
                    <dd>
                      {statusLabel(selectedDocument.extraction_status)} /{" "}
                      {statusLabel(selectedDocument.ocr_status)}
                    </dd>
                    <dt>Chunks</dt>
                    <dd>{selectedDocument.chunk_count || 0}</dd>
                    <dt>R2 object</dt>
                    <dd>{shortText(selectedDocument.r2_object_key)}</dd>
                    <dt>Source page</dt>
                    <dd>{shortText(selectedDocument.canonical_url)}</dd>
                    <dt>Original URL</dt>
                    <dd>{shortText(selectedDocument.original_url)}</dd>
                  </dl>
                </div>

                <div className="admin-review-panel">
                  <label>
                    Reviewer
                    <input
                      value={reviewer}
                      onChange={(event) => setReviewer(event.target.value)}
                    />
                  </label>
                  <label>
                    Review notes
                    <textarea
                      value={reviewNotes}
                      onChange={(event) => setReviewNotes(event.target.value)}
                    />
                  </label>
                  <div className="admin-review-actions">
                    <button type="button" onClick={saveEdits} disabled={isSaving}>
                      Save Edits
                    </button>
                    {REVIEW_ACTIONS.filter(
                      (action) =>
                        REVIEW_STATUS_BY_ACTION[action.id] !==
                        selectedDocument.review_status,
                    ).map((action) => (
                      <button
                        key={action.id}
                        type="button"
                        onClick={() => applyReviewAction(action.id)}
                        disabled={isSaving}
                      >
                        {action.label}
                      </button>
                    ))}
                  </div>
                </div>
              </>
            ) : (
              <div className="admin-empty">Select a document.</div>
            )}
          </section>
        </section>

        <section className="admin-evidence-panel">
          <div className="admin-evidence-controls">
            <h2>Approved Evidence Preview</h2>
            {["country", "region", "city"].map((field) => (
              <input
                key={field}
                value={evidenceLocation[field]}
                onChange={(event) =>
                  setEvidenceLocation((prev) => ({
                    ...prev,
                    [field]: event.target.value,
                  }))
                }
                placeholder={field}
              />
            ))}
            <button type="button" onClick={loadApprovedEvidence}>
              Preview
            </button>
          </div>
          <div className="admin-evidence-list">
            {approvedEvidence.map((item) => (
              <article key={item.document_id}>
                <strong>{shortText(item.document_title)}</strong>
                <span>
                  {shortText(item.source_name)} · score{" "}
                  {item.location_relevance_score || 0} · {statusLabel(item.category)}
                </span>
              </article>
            ))}
            {!approvedEvidence.length ? (
              <div className="admin-empty">No approved evidence loaded.</div>
            ) : null}
          </div>
        </section>
      </section>
    </main>
  );
}

export default AdminMarketSourcesScreen;
